<template>
	<div
		:data-id="item.id"
		:class="cardClasses"
		class="card"
		data-cy="card"
		tabindex="0"
		@keyup.enter="cardClick"
		@keyup.space="cardClick"
		@click="cardClick"
	>
		<div
			v-if="item.status"
			class="card__status"
		>
			<div class="card__status-icon">
				<helio-icon
					v-if="item.status.iconToken"
					:token="item.status.iconToken"
					:weight="item.status.iconWeight"
				/>
			</div>

			<p
				v-if="item.status.text"
				class="card__status-text"
			>
				{{ item.status.text }}
			</p>
		</div>

		<div class="card__container">
			<div class="card__main">
				<div
					class="card__images"
					@click="$emit('open', $event)"
				>
					<div
						v-if="item.image"
						:style="{ 'background-image': 'url(' + item.image + ')' }"
						class="card__image"
					/>

					<helio-default-image
						v-if="!item.image"
						color="sky-gray3"
						class="card__default-image"
						:roundness="3"
						size="small"
					/>
				</div>
				<div
					class="card__informations"
					@click="$emit('open', $event)"
				>
					<div
						v-if="widthMode !== 'regular'"
						class="card__informations-top"
					>
						<div
							v-if="item.author"
							class="card__author"
						>
							<helio-avatar
								v-if="item.author.image || item.author.name"
								:animation="true"
								:tooltip="item.author.name"
								:user="user"
								size="small"
							/>
						</div>

						<div
							v-if="item.tags && widthMode === 'mobile'"
							class="card__tags"
						>
							<div
								v-for="(tag, index) in item.tags"
								:key="index"
								class="card__tag"
								data-cy="card-tag"
							>
								<helio-icon
									v-if="tag.iconToken"
									:token="tag.iconToken"
									:weight="tag.iconWeight"
								/>{{ tag.text }}
							</div>
						</div>
					</div>

					<div class="card__informations-middle">
						<div
							v-if="item.taxonomy"
							class="card__taxonomy-container"
						>
							<div
								v-for="(taxonomyItem, index) in taxonomy"
								:key="index"
								class="card__taxonomy"
							>
								<helio-icon
									v-if="taxonomyItem.iconToken"
									:token="taxonomyItem.iconToken"
									:weight="taxonomyItem.iconWeight"
								/>

								<router-link
									v-if="taxonomyItem.link"
									:to="taxonomyItem.link"
									tabindex="-1"
								>
									{{ taxonomyItem.text }}
								</router-link>

								<span
									v-else
									data-cy="card-taxonomy"
								>
									{{ taxonomyItem.text }}
								</span>
							</div>
						</div>

						<div
							v-if="skeleton"
							class="card__taxonomy"
						>
							&nbsp;
						</div>

						<h2
							v-if="skeleton"
							class="card__title"
							data-cy="card-title"
						/>

						<h2
							v-if="item.title"
							class="card__title"
							data-cy="card-title"
							@click="$emit('open', $event)"
						>
							{{ item.title }}
						</h2>

						<p
							v-if="item.excerpt"
							class="card__excerpt"
						>
							{{ excerpt }}
						</p>

						<p
							v-if="skeleton"
							class="card__excerpt"
						/>

						<p
							v-if="skeleton"
							class="card__excerpt"
						/>

						<p
							v-if="skeleton"
							class="card__excerpt"
						/>
					</div>

					<div class="card__informations-bottom">
						<helio-badge
							v-for="(badge, index) in item.badges"
							:key="index"
							:color="badge.color"
							class="card__badge"
						>
							{{ badge.text }}
						</helio-badge>

						<div
							v-if="(item.tags || skeleton) && (widthMode == 'small' || widthMode == 'medium')"
							class="card__tags"
							data-cy="card-tags"
						>
							<div
								v-for="(tag, index) in item.tags"
								:key="index"
								class="card__tag"
								data-cy="card-tag"
							>
								<helio-icon
									v-if="tag.iconToken"
									:token="tag.iconToken"
									:weight="tag.iconWeight"
								/>{{ tag.text }}
							</div>
						</div>
					</div>
				</div>

				<div
					v-if="widthMode === 'regular'"
					class="card__middle-right"
				>
					<div
						v-if="item.author"
						class="card__author"
					>
						<helio-avatar
							v-if="item.author.image || item.author.name"
							:animation="true"
							:tooltip="item.author.name"
							:user="user"
							size="small"
						/>
					</div>

					<div
						v-if="skeleton"
						class="card__author"
					/>

					<div
						v-if="item.tags"
						class="card__tags"
					>
						<div
							v-for="(tag, index) in item.tags"
							:key="index"
							class="card__tag"
							data-cy="card-tag"
						>
							<helio-icon
								v-if="tag.iconToken"
								:token="tag.iconToken"
								:weight="tag.iconWeight"
							/>{{ tag.text }}
						</div>
					</div>

					<div
						v-if="skeleton"
						class="card__tags"
					/>
				</div>
			</div>

			<div
				v-if="hasDefaultSlot"
				class="card__options"
			>
				<slot :item="item" />
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';
import Badge from '../badge/Badge.vue';
import Avatar from '../avatar/Avatar.vue';
import DefaultImage from '../default-image/DefaultImage.vue';

export default {
	components: {
		'helio-icon': Icon,
		'helio-badge': Badge,
		'helio-avatar': Avatar,
		'helio-default-image': DefaultImage,
	},
	props: {
		item: {
			type: Object,
			required: false,
			default: () => ({
				not: 'undefined',
			}),
			description: 'Supplies content information to the Card component',
		},
		skeleton: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether a placeholder skeleton will be shown when the Card is loading',
		},
		widthMode: {
			type: String,
			required: false,
			default: 'regular',
			description: 'Sets the width of the Card',
		},
		isIe: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Indicates whether the user is using Internet Explorer',
		},
	},
	data() {
		return {
			noAnimation: false,
		};
	},
	computed: {
		cardClasses() {
			let classes = ` card--${this.widthMode}`;

			if (this.skeleton) {
				classes += ' skeleton';
				if (this.noAnimation) {
					classes += ' no-animation';
				}
			}

			if (this.item.border !== undefined) {
				classes += ` card--border-${this.item.border}`;
			}

			if (this.item.status) {
				classes += ' card--has-status';
				classes += ` card--status-${this.item.status.color}`;
			}

			return classes;
		},

		excerpt() {
			const text = this.item.excerpt;

			if (this.isIe) {
				if (this.widthMode === 'mobile') {
					return `${text.substring(0, 150)}\xa0...`;
				}
				if (this.widthMode === 'small') {
					return `${text.substring(0, 75)}\xa0...`;
				}
				if (this.widthMode === 'medium') {
					return `${text.substring(0, 225)}\xa0...`;
				}
			}
			return text;
		},

		taxonomy() {
			return Array.isArray(this.item.taxonomy) ? this.item.taxonomy : [this.item.taxonomy];
		},

		user() {
			const user = {};

			if (this.item.author.image) {
				user.image = this.item.author.image;
			}

			return user;
		},
	},

	methods: {
		cardClick() {
			this.$emit('click');
			this.noAnimation = true;
		},

		hasDefaultSlot() {
			return !!this.$slots.default || !!this.$scopedSlots.default;
		},
	},
};
</script>

<style lang="scss" src="./card.scss" />
